import type { Edge, Node } from '../../../core/models/scene';
import type { OrgModelEntity, OrgModelProjection } from '../models/OrgModelProjection';
import { stableExternalIdSegment } from '../../knowledge-ingestion/services/SourceMappingMaterializer';
import { isPersonNodeType, resolveOrgNodeType } from '../fields/entityFieldPacks';

/**
 * Deterministic canvas-node → folder-record matching for org charts.
 *
 * Fresh charts (canvas CSV import, workspace-built charts) carry no
 * `attrs.folderRecordId`, so record-gated surfaces such as succession
 * drafting never enable. This service computes the missing links against the
 * permissioned org-model projection so callers can stamp the record UUID —
 * and only the UUID — onto the shape. Protected record values never flow
 * through this path.
 *
 * Matching is intentionally conservative: exact identity keys first
 * (externalId / bound source record id), then the identity segment derived
 * from the node's e-mail (permissioned projections redact e-mail props, but
 * materialized externalIds end in `stableExternalIdSegment(email)`), then
 * unique e-mail for people, then unique incumbent inference for positions.
 * Ambiguous candidates are skipped and existing `folderRecordId` values are
 * never overwritten.
 */

export interface OrgChartRecordLink {
  nodeId: string;
  folderRecordId: string;
}

export type OrgChartRecordLinkModel = Pick<OrgModelProjection, 'people' | 'positions' | 'positionState'>;

const POSITION_NODE_TYPES = new Set(['org_position', 'position', 'role']);

export function computeOrgChartRecordLinks(
  nodes: readonly Node[],
  edges: readonly Edge[],
  model: OrgChartRecordLinkModel,
): OrgChartRecordLink[] {
  const peopleByKey = buildKeyIndex(model.people);
  const positionsByKey = buildKeyIndex(model.positions);
  const peopleByEmail = buildUniqueEmailIndex(model.people);
  const peopleByIdentitySegment = buildIdentitySegmentIndex(model.people);
  const positionsByOccupant = buildOccupantIndex(model);

  const links: OrgChartRecordLink[] = [];
  const personRecordIdByNodeId = new Map<string, string>();

  for (const node of nodes) {
    const type = resolveOrgNodeType(node);
    if (!isPersonNodeType(type)) continue;
    const existing = existingRecordId(node);
    if (existing) {
      personRecordIdByNodeId.set(node.id, existing);
      continue;
    }
    const entity = matchByIdentityKeys(node, 'person', peopleByKey)
      ?? matchByIdentitySegment(node, peopleByIdentitySegment)
      ?? matchByEmail(node, peopleByEmail);
    if (!entity) continue;
    personRecordIdByNodeId.set(node.id, entity.id);
    links.push({ nodeId: node.id, folderRecordId: entity.id });
  }

  const occupantNodeIdsByPositionNodeId = buildCanvasOccupancy(edges);
  for (const node of nodes) {
    if (!POSITION_NODE_TYPES.has(resolveOrgNodeType(node))) continue;
    if (existingRecordId(node)) continue;
    const direct = matchByIdentityKeys(node, 'position', positionsByKey);
    if (direct) {
      links.push({ nodeId: node.id, folderRecordId: direct.id });
      continue;
    }
    const inferred = inferPositionFromIncumbents(
      node.id,
      occupantNodeIdsByPositionNodeId,
      personRecordIdByNodeId,
      positionsByOccupant,
    );
    if (inferred) links.push({ nodeId: node.id, folderRecordId: inferred });
  }

  return links;
}

function existingRecordId(node: Node): string | null {
  const value = node.attrs?.folderRecordId;
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function buildKeyIndex(entities: readonly OrgModelEntity[]): Map<string, OrgModelEntity> {
  const index = new Map<string, OrgModelEntity>();
  for (const entity of entities) {
    index.set(entity.id, entity);
    if (entity.externalId) index.set(entity.externalId, entity);
  }
  return index;
}

/** E-mail → entity, dropping addresses shared by more than one record. */
function buildUniqueEmailIndex(entities: readonly OrgModelEntity[]): Map<string, OrgModelEntity> {
  const index = new Map<string, OrgModelEntity | null>();
  for (const entity of entities) {
    const email = normalizedEmail(entity.props.email ?? entity.props.workEmail);
    if (!email) continue;
    index.set(email, index.has(email) ? null : entity);
  }
  const unique = new Map<string, OrgModelEntity>();
  for (const [email, entity] of index) {
    if (entity) unique.set(email, entity);
  }
  return unique;
}

/** Person record UUID → position record UUIDs the person currently occupies. */
function buildOccupantIndex(model: OrgChartRecordLinkModel): Map<string, string[]> {
  const index = new Map<string, string[]>();
  for (const state of model.positionState) {
    for (const occupant of state.occupantPersonIds) {
      index.set(occupant, [...(index.get(occupant) ?? []), state.positionId]);
    }
  }
  return index;
}

/** Position canvas node id → occupant person canvas node ids. */
function buildCanvasOccupancy(edges: readonly Edge[]): Map<string, string[]> {
  const index = new Map<string, string[]>();
  for (const edge of edges) {
    if (!isOccupiesEdge(edge)) continue;
    index.set(edge.to, [...(index.get(edge.to) ?? []), edge.from]);
  }
  return index;
}

function isOccupiesEdge(edge: Edge): boolean {
  if (String(edge.type) === 'occupies') return true;
  const relation = edge.attrs?.predicate ?? edge.attrs?.relation;
  return typeof relation === 'string' && relation === 'occupies';
}

function matchByIdentityKeys(
  node: Node,
  kind: 'person' | 'position',
  index: ReadonlyMap<string, OrgModelEntity>,
): OrgModelEntity | null {
  for (const key of identityKeyCandidates(node, kind)) {
    const entity = index.get(key);
    if (entity) return entity;
  }
  return null;
}

/**
 * Identity keys a canvas node may carry, strongest first. `dataBinding.recordId`
 * is the bound source record id — for folder-record backed sources that is the
 * record's externalId. Kind-specific: person nodes also carry a `positionId`
 * attr describing their seat, which must never be used as the person's own
 * identity.
 */
function identityKeyCandidates(node: Node, kind: 'person' | 'position'): string[] {
  const attrs = node.attrs ?? {};
  const props = (node.props ?? {}) as Record<string, unknown>;
  const candidates = [
    attrs.externalId,
    ...(kind === 'person' ? [attrs.personId] : [attrs.positionId, attrs.code]),
    bindingRecordId(props.dataBinding),
    bindingRecordId(attrs.dataBinding),
  ];
  return candidates
    .filter((value): value is string => typeof value === 'string')
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
}

function bindingRecordId(binding: unknown): string | null {
  if (!binding || typeof binding !== 'object' || Array.isArray(binding)) return null;
  const recordId = (binding as Record<string, unknown>).recordId;
  return typeof recordId === 'string' ? recordId : null;
}

function matchByEmail(
  node: Node,
  index: ReadonlyMap<string, OrgModelEntity>,
): OrgModelEntity | null {
  const email = normalizedEmail(node.attrs?.email);
  return email ? index.get(email) ?? null : null;
}

/**
 * Materialized externalIds end in `stableExternalIdSegment(identityValue)`
 * (typically the e-mail). Permissioned projections redact identity props, but
 * the node holds the raw value — re-derive the segment and match on it.
 * Segments carry an exact content hash, so collisions require identical raw
 * identity values; duplicated segments are dropped from the index.
 */
function buildIdentitySegmentIndex(entities: readonly OrgModelEntity[]): Map<string, OrgModelEntity> {
  const index = new Map<string, OrgModelEntity | null>();
  for (const entity of entities) {
    if (!entity.externalId) continue;
    const segment = entity.externalId.slice(entity.externalId.lastIndexOf(':') + 1);
    if (!segment) continue;
    index.set(segment, index.has(segment) ? null : entity);
  }
  const unique = new Map<string, OrgModelEntity>();
  for (const [segment, entity] of index) {
    if (entity) unique.set(segment, entity);
  }
  return unique;
}

function matchByIdentitySegment(
  node: Node,
  index: ReadonlyMap<string, OrgModelEntity>,
): OrgModelEntity | null {
  if (index.size === 0) return null;
  const attrs = node.attrs ?? {};
  const rawValues = [attrs.email, attrs.personId]
    .filter((value): value is string => typeof value === 'string')
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
  for (const raw of rawValues) {
    const entity = index.get(stableExternalIdSegment(raw))
      ?? index.get(stableExternalIdSegment(raw.toLowerCase()));
    if (entity) return entity;
  }
  return null;
}

function normalizedEmail(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  return normalized.includes('@') ? normalized : null;
}

/** A position node is linkable when its incumbents resolve to exactly one position record. */
function inferPositionFromIncumbents(
  positionNodeId: string,
  occupantNodeIdsByPositionNodeId: ReadonlyMap<string, string[]>,
  personRecordIdByNodeId: ReadonlyMap<string, string>,
  positionsByOccupant: ReadonlyMap<string, string[]>,
): string | null {
  const occupantNodeIds = occupantNodeIdsByPositionNodeId.get(positionNodeId) ?? [];
  const candidates = new Set<string>();
  for (const occupantNodeId of occupantNodeIds) {
    const personRecordId = personRecordIdByNodeId.get(occupantNodeId);
    if (!personRecordId) continue;
    for (const positionRecordId of positionsByOccupant.get(personRecordId) ?? []) {
      candidates.add(positionRecordId);
    }
  }
  if (candidates.size !== 1) return null;
  return [...candidates][0];
}
